<script lang="ts">
  import { onMount } from 'svelte';
  import { TenantBranding as BrandingApi, TenantWhatsAppApi } from '../lib/api';
  import {
    applyTenantBranding, readImageAsDataUrl, resolveLogoUrl,
    saveLocalBranding, loadLocalBranding, TOSCANA_LOGO,
    type TenantBranding, type LocalBrandingExt,
  } from '../lib/branding.svelte';
  import { reloadTenantContext } from '../lib/tenant.svelte';

  let { onSaved }: { onSaved?: () => void } = $props();
  let cargando = $state(true); let guardando = $state(false);
  let error = $state<string | null>(null); let aviso = $state<string | null>(null);
  let dragOver = $state(false);
  let pestana = $state<'tema' | 'navegacion' | 'identidad' | 'whatsapp'>('tema');

  let wa = $state({ enabled: false, numero: '', metaConectado: false, estado: 'apagado' });
  const waEstadoLabel = $derived(
    wa.metaConectado
      ? (wa.enabled ? 'Listo para enviar' : 'Conectado — apagado')
      : (wa.enabled ? 'Pendiente de conexión Toscana' : 'Apagado'),
  );

  let form = $state({
    logoUrl: '', nombreComercial: '',
    primaryColor: '#16a34a', accentColor: '#16a34a',
    textColor: '#16a34a',
    sidebarColor: '#14532d', sidebarTextColor: '#ffffff',
    sidebarActiveBg: '#15803d', sidebarHoverBg: '#333333',
    bgColor: '#dcf5f0',
    navbarBg: '#ffffff', navbarText: '#16a34a',
    profileImage: '',
    horario: '', direccion: '', telefono: '', email: '', descripcion: '',
  });

  const previewLogo = $derived(resolveLogoUrl(form as any));

  function aplicar() {
    applyTenantBranding(form as any);
    const r = document.documentElement;
    r.style.setProperty('--app-bg', form.bgColor);
    r.style.setProperty('--brand-deep', form.textColor);
    r.style.setProperty('--sidebar-bg', form.sidebarColor);
    r.style.setProperty('--sidebar-text', form.sidebarTextColor);
    r.style.setProperty('--sidebar-active-bg', form.sidebarActiveBg);
    r.style.setProperty('--sidebar-hover-bg', form.sidebarHoverBg);
    r.style.setProperty('--navbar-bg', form.navbarBg);
    r.style.setProperty('--navbar-text', form.navbarText);
    r.style.setProperty('--brand-on', '#ffffff');
    // Detectar si el fondo es oscuro y ajustar texto
    const isDark = form.bgColor && parseInt(form.bgColor.slice(1,3), 16) < 80;
    r.style.setProperty('--text-body', isDark ? '#e2e8f0' : '#1e293b');
    if (isDark) {
      r.style.setProperty('--glass-bg-card', 'rgba(30,41,59,0.65)');
      r.style.setProperty('--glass-border', 'rgba(148,163,184,0.15)');
      r.style.setProperty('--glass-blur', 'blur(18px)');
    } else {
      r.style.removeProperty('--glass-bg-card');
      r.style.removeProperty('--glass-border');
      r.style.removeProperty('--glass-blur');
    }
    saveLocalBranding(form as LocalBrandingExt);
    window.dispatchEvent(new CustomEvent('toscana-branding-updated'));
  }

  $effect(() => { if (!cargando && form.primaryColor) aplicar(); });

  async function cargar() {
    cargando = true; error = null;
    try {
      const r = await BrandingApi.obtener(); const b = r.branding ?? {};
      const local = loadLocalBranding();
      form = {
        logoUrl: (b as any).logoUrl ?? local.logoUrl ?? '',
        nombreComercial: (b as any).nombreComercial ?? r.nombre ?? '',
        primaryColor: (b as any).primaryColor ?? local.primaryColor ?? '#16a34a',
        accentColor: (b as any).accentColor ?? local.accentColor ?? '#16a34a',
        textColor: local.textColor ?? '#16a34a',
        sidebarColor: local.sidebarColor ?? '#14532d',
        sidebarTextColor: local.sidebarTextColor ?? '#ffffff',
        sidebarActiveBg: local.sidebarActiveBg ?? '#15803d',
        sidebarHoverBg: local.sidebarHoverBg ?? '#333333',
        bgColor: local.bgColor ?? '#dcf5f0',
        navbarBg: local.navbarBg ?? '#ffffff',
        navbarText: local.navbarText ?? '#16a34a',
        profileImage: local.profileImage ?? '',
        horario: local.horario ?? '', direccion: local.direccion ?? '',
        telefono: local.telefono ?? '', email: local.email ?? '', descripcion: local.descripcion ?? '',
      };
      aplicar();
      try {
        const w = await TenantWhatsAppApi.obtener();
        wa = {
          enabled: !!w.enabled,
          numero: w.numero ?? '',
          metaConectado: !!w.metaConectado,
          estado: w.estado ?? 'apagado',
        };
      } catch { /* WhatsApp opcional si API vieja */ }
    } catch (e: any) { error = e?.message ?? 'Error.'; }
    finally { cargando = false; }
  }

  async function guardar() {
    if (pestana === 'whatsapp') {
      await guardarWhatsApp();
      return;
    }
    guardando = true; error = null; aviso = null;
    try {
      await BrandingApi.actualizar({
        logoUrl: form.logoUrl?.trim() || null,
        primaryColor: form.primaryColor?.trim() || null,
        accentColor: form.accentColor?.trim() || null,
        nombreComercial: form.nombreComercial?.trim() || null,
      } as any);
      await reloadTenantContext();
      aviso = '✅ Identidad guardada.';
      onSaved?.();
    } catch (e: any) { error = e?.message ?? 'Error al guardar.'; }
    finally { guardando = false; }
  }

  async function guardarWhatsApp() {
    guardando = true; error = null; aviso = null;
    try {
      const w = await TenantWhatsAppApi.actualizar({
        enabled: wa.enabled,
        numero: wa.numero.trim(),
      });
      wa = {
        enabled: !!w.enabled,
        numero: w.numero ?? '',
        metaConectado: !!w.metaConectado,
        estado: w.estado ?? 'apagado',
      };
      aviso = wa.enabled
        ? (wa.metaConectado ? '✅ WhatsApp activado.' : '✅ Guardado. Toscana conectará su número en Meta.')
        : '✅ WhatsApp desactivado.';
      onSaved?.();
    } catch (e: any) { error = e?.message ?? 'Error al guardar WhatsApp.'; }
    finally { guardando = false; }
  }

  async function onLogoFile(ev: Event) {
    const input = ev.target as HTMLInputElement; const file = input.files?.[0];
    if (!file) return; error = null;
    try { form.logoUrl = await readImageAsDataUrl(file); } catch (e: any) { error = e?.message ?? 'Error.'; }
    finally { input.value = ''; }
  }
  function onDrop(ev: DragEvent) {
    ev.preventDefault(); dragOver = false;
    const file = ev.dataTransfer?.files?.[0];
    if (!file || !file.type.startsWith('image/')) { error = 'Solo imágenes PNG/JPG/WebP.'; return; }
    readImageAsDataUrl(file).then(u => form.logoUrl = u).catch(e => error = e?.message ?? 'Error.');
  }

  onMount(() => { void cargar(); });

  const TEMAS = [
    { name: 'Aqua', primary: '#16a34a', bg: '#dcf5f0', text: '#16a34a', sidebar: '#14532d' },
    { name: 'Azul', primary: '#2563eb', bg: '#eff6ff', text: '#1e40af', sidebar: '#0f172a' },
    { name: 'Verde', primary: '#059669', bg: '#ecfdf5', text: '#064e3b', sidebar: '#022c22' },
    { name: 'Violeta', primary: '#7c3aed', bg: '#faf5ff', text: '#5b21b6', sidebar: '#1e1b4b' },
    { name: 'Rosa', primary: '#db2777', bg: '#fdf2f8', text: '#9d174d', sidebar: '#1c1917' },
  ];

  // Avatares NO humanos: objetos, animales, robots, abstractos (DiceBear + Emoji)
  const AVATARS = [
    { name: 'Robot', svg: 'https://api.dicebear.com/9.x/bottts/svg?seed=medbot1&backgroundColor=0f766e' },
    { name: 'Gato', svg: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" rx="50" fill="#16a34a"/><text x="50" y="68" text-anchor="middle" font-size="50">🐱</text></svg>') },
    { name: 'Perro', svg: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" rx="50" fill="#ea580c"/><text x="50" y="68" text-anchor="middle" font-size="50">🐶</text></svg>') },
    { name: 'Robot 2', svg: 'https://api.dicebear.com/9.x/bottts/svg?seed=medbot2&backgroundColor=0f766e' },
    { name: 'Estrella', svg: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" rx="50" fill="#fbbf24"/><text x="50" y="68" text-anchor="middle" font-size="50">⭐</text></svg>') },
    { name: 'Corazón', svg: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" rx="50" fill="#ec4899"/><text x="50" y="68" text-anchor="middle" font-size="50">❤️</text></svg>') },
    { name: 'Abstracto', svg: 'https://api.dicebear.com/9.x/shapes/svg?seed=hosp1&backgroundColor=0f766e' },
    { name: 'Geométrico', svg: 'https://api.dicebear.com/9.x/identicon/svg?seed=hosp2&backgroundColor=10b981' },
    { name: 'Sol', svg: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" rx="50" fill="#f97316"/><text x="50" y="68" text-anchor="middle" font-size="50">☀️</text></svg>') },
    { name: 'Anillos', svg: 'https://api.dicebear.com/9.x/rings/svg?seed=hosp3&backgroundColor=6366f1' },
    { name: 'Luna', svg: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" rx="50" fill="#6366f1"/><text x="50" y="68" text-anchor="middle" font-size="50">🌙</text></svg>') },
    { name: 'Diamante', svg: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" rx="50" fill="#06b6d4"/><text x="50" y="68" text-anchor="middle" font-size="50">💎</text></svg>') },
  ];
  function aplicarTema(t: typeof TEMAS[number]) {
    form.primaryColor = t.primary; form.bgColor = t.bg;
    form.textColor = t.text; form.sidebarColor = t.sidebar;
    form.sidebarTextColor = '#ffffff'; form.sidebarActiveBg = t.primary;
    const isDark = t.bg.startsWith('#1') || t.bg.startsWith('#0');
    form.navbarBg = isDark ? '#1f2937' : '#ffffff';
    form.navbarText = isDark ? '#e2e8f0' : t.text;
    form.sidebarHoverBg = isDark ? '#374151' : '#f3f4f6';
  }

  const PALETA = ['#15803d','#16a34a','#059669','#2563eb','#7c3aed','#dc2626','#ea580c','#ca8a04','#6366f1','#db2777','#111827','#1e1b4b'];
  const FONDOS = ['#dcf5f0','#e6fafb','#f0fdf4','#eff6ff','#faf5ff','#fdf2f8','#fff7ed','#f8fafc','#111827','#1e1b4b'];

  function colorField(label: string, bind: string, swatches?: string[]) {
    return { label, bind, swatches };
  }
</script>

<div class="branding-page">
  {#if cargando}
    <div class="loading">⏳ Cargando editor de temas...</div>
  {:else}
    {#if error}<div class="msg msg-error">{error}</div>{/if}
    {#if aviso}<div class="msg msg-ok">{aviso}</div>{/if}

    <!-- Temas predefinidos -->
    <div class="presets-bar">
      <span class="presets-label">Temas rápidos:</span>
      {#each TEMAS as t}
        <button class="preset-chip" style="--c:{t.primary}" onclick={() => aplicarTema(t)}>
          <span class="preset-dot" style="background:{t.primary}"></span>
          {t.name}
        </button>
      {/each}
    </div>

    <div class="layout">
      <!-- PREVIEW -->
      <div class="preview-col">
        <div class="preview-phone" style="background:{form.bgColor}">
          <!-- Navbar preview -->
          <div class="preview-navbar" style="background:{form.navbarBg}">
            <span class="preview-nav-icon">☰</span>
            <span class="preview-nav-title" style="color:{form.navbarText}">Pacientes</span>
            <span class="preview-nav-user" style="color:{form.navbarText}">👤</span>
          </div>
          <div class="preview-body">
            <!-- Sidebar -->
            <div class="preview-sidebar" style="background:{form.sidebarColor}">
              <div class="prev-side-top">
                {#if previewLogo}
                  <img src={previewLogo} alt="" class="prev-logo" />
                {:else}
                  <div class="prev-avatar" style="background:linear-gradient(145deg,{form.primaryColor},{form.accentColor})">
                    {(form.nombreComercial||'HO').slice(0,2).toUpperCase()}
                  </div>
                {/if}
                <div class="prev-name" style="color:{form.sidebarTextColor}">{form.nombreComercial || 'Mi Hospital'}</div>
              </div>
              <div class="prev-menu">
                <div class="prev-menu-title" style="color:{form.sidebarTextColor}88">INICIO</div>
                <div class="prev-menu-item active" style="background:{form.sidebarActiveBg}cc;color:{form.sidebarTextColor}">Dashboard</div>
                <div class="prev-menu-title" style="color:{form.sidebarTextColor}88">ATENCIÓN</div>
                <div class="prev-menu-item" style="color:{form.sidebarTextColor}cc">Agenda</div>
                <div class="prev-menu-item hover" style="background:{form.sidebarHoverBg}1a;color:{form.sidebarTextColor}cc">Pacientes</div>
              </div>
            </div>
            <!-- Content -->
            <div class="preview-content">
              <div class="prev-card">
                <div class="prev-card-hd" style="color:{form.textColor}">Pacientes</div>
                <div class="prev-card-line" style="color:{form.textColor}99">María López · CUI 1234567890101</div>
                <div class="prev-card-line" style="color:{form.textColor}99">Carlos Ruiz · CUI 9876543210909</div>
                <button class="prev-btn" style="background:{form.primaryColor}">+ Registrar</button>
              </div>
              <div class="prev-card">
                <div class="prev-card-line" style="color:{form.textColor}99">Próxima cita: 10:30 AM</div>
                <div class="prev-card-line" style="color:{form.textColor}99">Dr. Martínez · Consultorio 3</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CONTROLES -->
      <div class="controls-col">
        <div class="tab-bar">
          <button class="tab" class:active={pestana === 'tema'} onclick={() => pestana = 'tema'}>🎨 Tema</button>
          <button class="tab" class:active={pestana === 'navegacion'} onclick={() => pestana = 'navegacion'}>🧭 Navegación</button>
          <button class="tab" class:active={pestana === 'identidad'} onclick={() => pestana = 'identidad'}>🏷️ Identidad</button>
          <button class="tab" class:active={pestana === 'whatsapp'} onclick={() => pestana = 'whatsapp'}>💬 WhatsApp</button>
        </div>

        {#if pestana === 'whatsapp'}
          <div class="ctrl-section">
            <div class="ctrl-title">WhatsApp de su hospital</div>
            <p class="ctrl-hint">Active el canal e indique el número. Toscana conecta Meta por detrás; usted no configura tokens.</p>
            <label class="wa-switch">
              <input type="checkbox" bind:checked={wa.enabled} />
              <span>{wa.enabled ? 'Activado' : 'Desactivado'}</span>
            </label>
            <label class="wa-num-label">Número WhatsApp</label>
            <input class="text-input" bind:value={wa.numero} placeholder="50255551234" inputmode="tel" autocomplete="tel" />
            <div class="wa-status" class:ok={wa.metaConectado && wa.enabled} class:warn={wa.enabled && !wa.metaConectado}>
              Estado: {waEstadoLabel}
            </div>
          </div>
        {:else if pestana === 'tema'}
          <div class="ctrl-section">
            <div class="ctrl-title">Color principal</div>
            <p class="ctrl-hint">Botones, links, elementos interactivos</p>
            <div class="color-input">
              <input type="color" bind:value={form.primaryColor} />
              <input class="hex" bind:value={form.primaryColor} maxlength="7" />
            </div>
            <div class="swatches">
              {#each PALETA as c}
                <button class="sw" style="background:{c}" onclick={() => form.primaryColor = c} class:sel={form.primaryColor === c} title={c}></button>
              {/each}
            </div>
          </div>

          <div class="ctrl-section">
            <div class="ctrl-title">Color de texto</div>
            <p class="ctrl-hint">Títulos, encabezados, texto destacado</p>
            <div class="color-input">
              <input type="color" bind:value={form.textColor} />
              <input class="hex" bind:value={form.textColor} maxlength="7" />
            </div>
          </div>

          <div class="ctrl-section">
            <div class="ctrl-title">Fondo de página</div>
            <p class="ctrl-hint">Fondo general detrás de las tarjetas</p>
            <div class="color-input">
              <input type="color" bind:value={form.bgColor} />
              <input class="hex" bind:value={form.bgColor} maxlength="7" />
            </div>
            <div class="swatches">
              {#each FONDOS as c}
                <button class="sw" style="background:{c}" onclick={() => form.bgColor = c} class:sel={form.bgColor === c} title={c}></button>
              {/each}
            </div>
          </div>

          <div class="ctrl-section">
            <div class="ctrl-title">Barra lateral</div>
            <div class="color-grid-2">
              <label class="color-field"><span>Fondo</span>
                <div class="color-input"><input type="color" bind:value={form.sidebarColor} /><input class="hex" bind:value={form.sidebarColor} maxlength="7" /></div>
              </label>
              <label class="color-field"><span>Texto</span>
                <div class="color-input"><input type="color" bind:value={form.sidebarTextColor} /><input class="hex" bind:value={form.sidebarTextColor} maxlength="7" /></div>
              </label>
              <label class="color-field"><span>Ítem activo</span>
                <div class="color-input"><input type="color" bind:value={form.sidebarActiveBg} /><input class="hex" bind:value={form.sidebarActiveBg} maxlength="7" /></div>
              </label>
              <label class="color-field"><span>Hover</span>
                <div class="color-input"><input type="color" bind:value={form.sidebarHoverBg} /><input class="hex" bind:value={form.sidebarHoverBg} maxlength="7" /></div>
              </label>
            </div>
          </div>

          <div class="ctrl-section">
            <div class="ctrl-title">Barra superior</div>
            <div class="color-grid-2">
              <label class="color-field"><span>Fondo</span>
                <div class="color-input"><input type="color" bind:value={form.navbarBg} /><input class="hex" bind:value={form.navbarBg} maxlength="7" /></div>
              </label>
              <label class="color-field"><span>Texto</span>
                <div class="color-input"><input type="color" bind:value={form.navbarText} /><input class="hex" bind:value={form.navbarText} maxlength="7" /></div>
              </label>
            </div>
          </div>
        {:else}
          <div class="ctrl-section">
            <div class="ctrl-title">Nombre comercial</div>
            <input class="text-input" bind:value={form.nombreComercial} placeholder="Hospital El Sol" />
          </div>

          <div class="ctrl-section">
            <div class="ctrl-title">Imagen de perfil</div>
            <div class="profile-row">
              <div class="profile-preview-lg">
                {#if form.profileImage}
                  <img src={form.profileImage} alt="" />
                {:else}
                  <span>👤</span>
                {/if}
              </div>
              <div class="profile-info">
                <span class="profile-name">Vista previa</span>
                <label class="dz-btn-lg">📁 Subir foto
                  <input type="file" accept="image/*" onchange={(e) => {
                    const f = (e.target as HTMLInputElement).files?.[0];
                    if (!f) return;
                    const r = new FileReader();
                    r.onload = () => form.profileImage = String(r.result);
                    r.readAsDataURL(f);
                  }} hidden />
                </label>
                {#if form.profileImage}
                  <button class="text-btn" onclick={() => form.profileImage = ''}>Quitar imagen</button>
                {/if}
              </div>
            </div>
            <div class="avatar-section-label">Avatares predefinidos</div>
            <div class="avatar-grid-lg">
              {#each AVATARS as av}
                <button class="avatar-card" class:sel={form.profileImage === av.svg} onclick={() => form.profileImage = av.svg} title={av.name}>
                  <img src={av.svg} alt={av.name} />
                  <span>{av.name}</span>
                </button>
              {/each}
            </div>
          </div>

          <div class="ctrl-section">
            <div class="ctrl-title">Logo</div>
            <input class="text-input" bind:value={form.logoUrl} placeholder="https://.../logo.svg" />
            <div class="dropzone" class:over={dragOver} role="button" tabindex="0"
                 ondragover={(e) => { e.preventDefault(); dragOver = true; }} ondragleave={() => dragOver = false} ondrop={onDrop}>
              <span class="dz-icon">📁</span>
              <span>Arrastrá un logo o</span>
              <label class="dz-btn">Seleccionar archivo <input type="file" accept="image/png,image/jpeg,image/webp" onchange={onLogoFile} hidden /></label>
            </div>
          </div>

          <div class="ctrl-section">
            <div class="ctrl-title">Datos del hospital</div>
            <div class="info-fields">
              <input class="text-input" bind:value={form.horario} placeholder="🕐 Horario: Lun-Vie 8am-6pm" />
              <input class="text-input" bind:value={form.telefono} placeholder="📞 Teléfono: +502 5555-1234" />
              <input class="text-input" bind:value={form.direccion} placeholder="📍 Dirección" />
              <input class="text-input" bind:value={form.email} placeholder="✉️ Email" />
              <input class="text-input" bind:value={form.descripcion} placeholder="📝 Descripción corta" />
            </div>
          </div>
        {/if}

        <button class="save-btn" disabled={guardando} onclick={guardar}>
          {guardando ? '⏳ Guardando...' : (pestana === 'whatsapp' ? '💾 Guardar WhatsApp' : '💾 Guardar cambios')}
        </button>
      </div>
    </div>
  {/if}
</div>

<style>
  :global(:root) {
    --glass-bg-card: rgba(255,255,255,0.65);
    --glass-border: rgba(13,148,136,0.12);
    --glass-blur: blur(18px);
    --glass-shadow: 0 4px 20px rgba(13,148,136,0.05), 0 1px 3px rgba(0,0,0,0.03);
    --radius-xl: 1.25rem;
    --brand-surface: #f0fdfa;
    --text-muted: #94a3b8;
  }

  .branding-page { display: flex; flex-direction: column; gap: 0.85rem; }
  .loading { padding: 3rem; text-align: center; color: var(--text-muted); }
  .msg { padding: 0.6rem 0.9rem; border-radius: 0.7rem; font-size: 0.82rem; font-weight: 600; }
  .msg-error { background: rgba(239,68,68,0.08); color: #dc2626; border: 1px solid rgba(239,68,68,0.2); }
  .msg-ok { background: rgba(16,185,129,0.08); color: #059669; border: 1px solid rgba(16,185,129,0.2); }

  .wa-switch { display: flex; align-items: center; gap: 0.55rem; font-weight: 700; font-size: 0.9rem; color: var(--text-primary); margin: 0.75rem 0 1rem; cursor: pointer; }
  .wa-switch input { width: 1.15rem; height: 1.15rem; accent-color: var(--brand, #16a34a); }
  .wa-num-label { display: block; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-muted); margin-bottom: 0.35rem; }
  .wa-status { margin-top: 0.75rem; padding: 0.55rem 0.7rem; border-radius: 0.55rem; font-size: 0.78rem; font-weight: 600; background: rgba(148,163,184,0.12); color: var(--text-secondary); }
  .wa-status.ok { background: rgba(16,185,129,0.1); color: #059669; }
  .wa-status.warn { background: rgba(245,158,11,0.12); color: #b45309; }

  .presets-bar { display: flex; gap: 0.4rem; align-items: center; flex-wrap: wrap; padding: 0.65rem 0.85rem;
    background: var(--glass-bg-card); backdrop-filter: var(--glass-blur); -webkit-backdrop-filter: var(--glass-blur);
    border: 1px solid var(--glass-border); border-radius: var(--radius-xl); }
  .presets-label { font-size: 0.72rem; font-weight: 700; color: var(--text-muted); margin-right: 0.25rem; }
  .preset-chip { display: flex; align-items: center; gap: 0.35rem; padding: 0.35rem 0.7rem; border-radius: 999px;
    border: 1px solid var(--glass-border); background: var(--glass-bg-card); cursor: pointer;
    font: inherit; font-size: 0.75rem; font-weight: 600; color: var(--text-secondary); transition: all .15s; }
  .preset-chip:hover { border-color: var(--brand); background: var(--brand-surface); }
  .preset-dot { width: 10px; height: 10px; border-radius: 50%; }

  .layout { display: flex; gap: 1rem; align-items: flex-start; }
  .preview-col { flex: 1; min-width: 300px; max-width: 380px; position: sticky; top: 0.5rem; }
  .controls-col { flex: 1.2; min-width: 320px; display: flex; flex-direction: column; gap: 0.75rem; }

  /* Phone preview */
  .preview-phone { border-radius: 1.25rem; overflow: hidden; border: 3px solid rgba(0,0,0,0.1); box-shadow: 0 20px 50px rgba(0,0,0,0.12); }
  .preview-navbar { display: flex; align-items: center; justify-content: space-between; padding: 0.5rem 0.85rem; font-size: 0.75rem; }
  .preview-nav-title { font-weight: 700; }
  .preview-body { display: flex; min-height: 300px; }
  .preview-sidebar { width: 110px; padding: 0.65rem 0.5rem; display: flex; flex-direction: column; gap: 0.4rem; flex-shrink: 0; }
  .prev-side-top { display: flex; flex-direction: column; align-items: center; gap: 0.2rem; margin-bottom: 0.3rem; }
  .prev-avatar { width: 32px; height: 32px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 0.65rem; color: #fff; }
  .prev-logo { height: 28px; max-width: 70px; object-fit: contain; }
  .prev-name { font-size: 0.6rem; font-weight: 700; text-align: center; }
  .prev-menu { display: flex; flex-direction: column; gap: 0.1rem; }
  .prev-menu-title { font-size: 0.48rem; font-weight: 700; letter-spacing: 0.05em; padding: 0.2rem 0.2rem 0.05rem; }
  .prev-menu-item { padding: 0.25rem 0.35rem; border-radius: 0.3rem; font-size: 0.58rem; font-weight: 600; }
  .preview-content { flex: 1; padding: 0.55rem; display: flex; flex-direction: column; gap: 0.45rem; }
  .prev-card { background: rgba(255,255,255,0.55); border-radius: 0.55rem; padding: 0.5rem; }
  .prev-card-hd { font-size: 0.65rem; font-weight: 700; margin-bottom: 0.25rem; }
  .prev-card-line { font-size: 0.55rem; margin-bottom: 0.1rem; }
  .prev-btn { border: 0; border-radius: 0.3rem; padding: 0.3rem 0.5rem; color: #fff; font-size: 0.55rem; font-weight: 700; margin-top: 0.2rem; cursor: default; }

  /* Tabs */
  .tab-bar { display: flex; gap: 0.3rem; }
  .tab { flex: 1; padding: 0.55rem; border-radius: 0.6rem; border: 1px solid var(--glass-border);
    background: var(--glass-bg-card); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
    color: var(--text-secondary); cursor: pointer; font: inherit; font-size: 0.8rem; font-weight: 600; transition: all .2s; }
  .tab:hover { border-color: var(--brand); }
  .tab.active { background: var(--brand,#15803d); color: #fff; border-color: var(--brand,#15803d); }

  /* Controls */
  .ctrl-section { background: var(--glass-bg-card); backdrop-filter: var(--glass-blur); -webkit-backdrop-filter: var(--glass-blur);
    border: 1px solid var(--glass-border); border-radius: var(--radius-xl); padding: 1rem; box-shadow: var(--glass-shadow); }
  .ctrl-title { font-weight: 700; font-size: 0.83rem; color: var(--text-primary); margin-bottom: 0.15rem; }
  .ctrl-hint { font-size: 0.7rem; color: var(--text-muted); margin: 0 0 0.5rem; }

  .color-input { display: flex; gap: 0.4rem; align-items: center; margin-bottom: 0.45rem; }
  .color-input input[type="color"] { width: 2.5rem; height: 2.2rem; padding: 0; border: 2px solid var(--glass-border); border-radius: 0.4rem; background: none; cursor: pointer; }
  .hex { flex: 1; padding: 0.45rem 0.55rem; border: 1px solid var(--glass-border); border-radius: 0.5rem; background: rgba(255,255,255,0.6); font: inherit; font-family: monospace; font-size: 0.8rem; color: var(--text-primary); }
  .hex:focus { outline: none; border-color: var(--brand); }

  .swatches { display: flex; gap: 0.25rem; flex-wrap: wrap; }
  .sw { width: 18px; height: 18px; border-radius: 4px; border: 2px solid transparent; cursor: pointer; padding: 0; transition: transform .12s; }
  .sw:hover { transform: scale(1.3); }
  .sw.sel { border-color: var(--text-primary); box-shadow: 0 0 0 2px #fff; }

  .color-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0.55rem; }
  .color-field { display: flex; flex-direction: column; gap: 0.15rem; }
  .color-field span { font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-muted); }
  .color-field .color-input { margin-bottom: 0; }
  .color-field .color-input input[type="color"] { width: 2rem; height: 1.8rem; }
  .color-field .hex { font-size: 0.72rem; }

  .text-input { width: 100%; box-sizing: border-box; padding: 0.5rem 0.6rem; border: 1px solid var(--glass-border); border-radius: 0.55rem; background: rgba(255,255,255,0.6); font: inherit; font-size: 0.82rem; color: var(--text-primary); }
  .text-input:focus { outline: none; border-color: var(--brand); }
  .info-fields { display: flex; flex-direction: column; gap: 0.4rem; }

  .dropzone { display: flex; flex-direction: column; align-items: center; gap: 0.2rem; padding: 0.8rem; margin-top: 0.4rem; border: 2px dashed var(--glass-border); border-radius: 0.7rem; background: rgba(255,255,255,0.2); text-align: center; font-size: 0.75rem; color: var(--text-secondary); cursor: pointer; transition: all .2s; }
  .dropzone.over { border-color: var(--brand); background: color-mix(in srgb, var(--brand,#15803d) 4%, transparent); }
  .dz-icon { font-size: 1.3rem; }
  .dz-btn { padding: 0.3rem 0.7rem; border-radius: 999px; background: var(--brand,#15803d); color: #fff; font-weight: 700; font-size: 0.7rem; cursor: pointer; }

  .dz-btn-lg { padding: 0.45rem 0.85rem; border-radius: 999px; background: var(--brand,#15803d); color: #fff; font-weight: 700; font-size: 0.78rem; cursor: pointer; display: inline-block; width: fit-content; }

  .avatar-section-label { font-size: 0.72rem; font-weight: 700; color: var(--text-secondary); margin-bottom: 0.5rem; margin-top: 0.25rem; text-transform: uppercase; letter-spacing: 0.04em; }
  .avatar-grid-lg { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.55rem; }
  .avatar-card { display: flex; flex-direction: column; align-items: center; gap: 0.25rem; padding: 0.6rem 0.3rem;
    border: 2px solid transparent; border-radius: 10px; background: rgba(255,255,255,0.3); cursor: pointer; transition: all .15s; }
  .avatar-card:hover { border-color: var(--brand); background: var(--brand-surface); transform: translateY(-1px); }
  .avatar-card.sel { border-color: var(--brand); background: color-mix(in srgb, var(--brand,#15803d) 10%, transparent); box-shadow: 0 2px 12px color-mix(in srgb, var(--brand,#15803d) 20%, transparent); }
  .avatar-card img { width: 48px; height: 48px; border-radius: 10px; object-fit: cover; }
  .avatar-card span { font-size: 0.6rem; color: var(--text-secondary); font-weight: 600; }

  .save-btn { width: 100%; padding: 0.7rem; border: 0; border-radius: 0.7rem; background: linear-gradient(135deg, var(--brand,#15803d), var(--brand-deep,#16a34a)); color: #fff; font: inherit; font-size: 0.9rem; font-weight: 700; cursor: pointer; box-shadow: 0 6px 20px color-mix(in srgb, var(--brand,#15803d) 35%, transparent); transition: all .2s; margin-top: 0.25rem; }
  .save-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 28px color-mix(in srgb, var(--brand,#15803d) 45%, transparent); }
  .save-btn:disabled { opacity: .55; cursor: wait; }
</style>
